import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import {
  Book,
  RefreshCw,
  Settings,
  Clock,
  AlertCircle,
  CheckCircle,
  Loader2,
  Package,
  Calendar,
  MapPin,
  User,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";


export default function OrderBookPage() {
  const { user, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [cached, setCached] = useState(false);
  const [error, setError] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [cookieInput, setCookieInput] = useState("");
  const [savingCookies, setSavingCookies] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Check if user is Europacific staff
  const isStaff = user?.user_type === "europacific_staff";

  useEffect(() => {
    if (isStaff) {
      fetchOrders();
    }
  }, [isStaff]);

  const fetchOrders = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}/transbook/orders${forceRefresh ? '?force_refresh=true' : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders(response.data.orders || []);
      setFetchedAt(response.data.fetched_at);
      setCached(response.data.cached);
      if (response.data.error) {
        toast.warning(response.data.error);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      if (err.response?.status === 503) {
        setError("Session expired or not configured. Please set up Transbook session.");
        setShowSetup(true);
      } else {
        setError(err.response?.data?.detail || "Failed to fetch orders");
      }
    } finally {
      setLoading(false);
    }
  };

  const saveCookies = async () => {
    if (!cookieInput.trim()) {
      toast.error("Please paste the cookies JSON");
      return;
    }

    setSavingCookies(true);
    try {
      await axios.post(
        `${API_URL}/transbook/set-session`,
        { cookies: cookieInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Session saved! Fetching orders...");
      setShowSetup(false);
      setCookieInput("");
      fetchOrders(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save session");
    } finally {
      setSavingCookies(false);
    }
  };

  if (!isStaff) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] mt-16">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              Order Book is only available to Europacific staff members.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Book className="h-8 w-8" />
                <h1 className="text-3xl font-bold">Order Book</h1>
              </div>
              <p className="text-white/80">
                Transbook orders - Auto-refreshed every 4 hours
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSetup(!showSetup)}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <Settings className="h-4 w-4 mr-2" />
                Setup
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchOrders(true)}
                disabled={loading}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Setup Panel */}
        {showSetup && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl p-6 mb-6"
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Configure Transbook Session
            </h3>
            
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
                  How to get session cookies:
                </h4>
                <ol className="text-sm text-amber-700 dark:text-amber-300 space-y-2 list-decimal list-inside">
                  <li>Open <a href="https://ep-holding.transbook.onl/PmEpic/Browse" target="_blank" rel="noopener noreferrer" className="underline">Transbook</a> in a new tab</li>
                  <li>Login with Microsoft ("Sign in with Microsoft")</li>
                  <li>Once logged in, open DevTools (F12) → Application → Cookies</li>
                  <li>Right-click on the cookies list → Copy all as JSON</li>
                  <li>Paste the JSON below</li>
                </ol>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Paste Cookies JSON:
                </label>
                <textarea
                  value={cookieInput}
                  onChange={(e) => setCookieInput(e.target.value)}
                  placeholder='[{"name": "...", "value": "...", ...}]'
                  className="w-full h-32 p-3 rounded-lg border border-border bg-background font-mono text-sm"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={saveCookies}
                  disabled={savingCookies}
                >
                  {savingCookies ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Save Session
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowSetup(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Status Bar */}
        {fetchedAt && (
          <div className="flex items-center justify-between mb-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Last updated: {new Date(fetchedAt).toLocaleString()}
              {cached && (
                <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded text-xs">
                  Cached
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              {orders.length} orders
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <div>
                <h3 className="font-semibold text-red-800 dark:text-red-200">Error</h3>
                <p className="text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && orders.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Loading orders...</span>
          </div>
        )}

        {/* Orders Table */}
        {orders.length > 0 && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    {Object.keys(orders[0]).slice(0, 8).map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-left text-sm font-semibold text-foreground"
                      >
                        {header}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      className="border-b border-border hover:bg-muted/30 transition-colors"
                    >
                      {Object.values(order).slice(0, 8).map((value, i) => (
                        <td key={i} className="px-4 py-3 text-sm">
                          {String(value).substring(0, 50)}
                          {String(value).length > 50 && '...'}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedOrder(expandedOrder === index ? null : index)}
                        >
                          {expandedOrder === index ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && orders.length === 0 && !error && (
          <div className="text-center py-20">
            <Book className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Orders Found</h3>
            <p className="text-muted-foreground mb-4">
              Configure Transbook session to view orders
            </p>
            <Button onClick={() => setShowSetup(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Setup Session
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
